import { BCP47 } from './types'
import type { Locale, MessageKey, Messages, Params, PluralKey } from './types'

const PLACEHOLDER = /\{(\w+)\}/g

/**
 * 把 `{name}` 换成实参。
 *
 * 缺参数时**保留占位符原样**而不是留空：界面上看到 `{count}` 一眼就知道
 * 哪里漏了，而空字符串会伪装成一句读得通的话，一直没人发现。
 */
function interpolate(template: string, params?: Params): string {
  if (!params) return template
  return template.replace(PLACEHOLDER, (match, name: string) =>
    name in params ? String(params[name]) : match,
  )
}

export interface Translator {
  locale: Locale
  t(key: MessageKey, params?: Params): string
  /** 复数形式：按 Intl.PluralRules 选 `<key>.one` / `<key>.other` */
  tp(key: PluralKey, count: number, params?: Params): string
}

export function createTranslator(locale: Locale, messages: Messages): Translator {
  // 中日无复数变化（select(1) 也是 'other'），英文才会走到 'one'
  const rules = new Intl.PluralRules(BCP47[locale])

  const t = (key: MessageKey, params?: Params) => interpolate(messages[key], params)

  const tp = (key: PluralKey, count: number, params?: Params) => {
    const category = rules.select(count) === 'one' ? 'one' : 'other'
    const full = `${key}.${category}` as MessageKey
    // 只有英文会有 .one；词典里两个形式都要求存在，所以这里不用兜底
    return interpolate(messages[full], { count, ...params })
  }

  return { locale, t, tp }
}
